// API client for weern-vista-backend.
//
// Every response is parsed against a @weern/contracts schema (ADR-0026): the
// worker and this app share one runtime source, so a renamed field fails loudly
// here instead of rendering an empty column. Token lives in localStorage — no
// cookies, which is why the worker's CORS can stay a plain wildcard.
import type { ZodType } from 'zod';
import {
  VistaAddContributorsResSchema,
  VistaAdminStatsResSchema,
  VistaAssignResSchema,
  VistaAuthRequestResSchema,
  VistaAuthVerifyResSchema,
  VistaContributorResSchema,
  VistaCreateStaffResSchema,
  VistaInviteContributorResSchema,
  VistaLoginResSchema,
  VistaLiveNowResSchema,
  VistaMeResSchema,
  VistaMyStatsResSchema,
  VistaOkChangedResSchema,
  VistaPaceResSchema,
  VistaSetPaceGoalResSchema,
  VistaPoolResSchema,
  VistaStaffListResSchema,
  VistaSummaryResSchema,
  VistaTasksResSchema,
  VistaAdjustResSchema,
  VistaBalancesResSchema,
  VistaCaptureMoveResSchema,
  VistaCapturesResSchema,
  VistaLedgerResSchema,
  VistaMyLedgerResSchema,
  VistaPayoutResSchema,
  type VistaCaptureMove,
  type VistaRejectReason,
  VistaUnratedResSchema,
  VistaUnassignedResSchema,
  type VistaAddContributorsRes,
  type VistaAdminStatsRow,
  type VistaContributorRes,
  type VistaContributorMe,
  type VistaMeRes,
  type VistaMeRole,
  type VistaMyStatsRes,
  type VistaLiveNowRes,
  type VistaPaceRes,
  type VistaStaffMe,
  type VistaPoolRes,
  type VistaPoolRow,
  type VistaStaffListRes,
  type VistaSummaryRes,
  type VistaUnassignedRes,
  VistaVerdictsPreviewResSchema,
  VistaCaptureIndexResSchema,
} from '@weern/contracts';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';

/** Exported for the capture path: uploading a part is a raw-bytes PUT and
 *  cannot go through the JSON client below. */
export const API_BASE = BASE;
const TOKEN_KEY = 'vista_session';
// Second token, minted by POST /auth/as-contributor (F3): lets a staff person
// who ALSO owns a contributors row (an admin/manager who shoots their own
// material) drive /my/* and /capture/* with a genuine contributor JWT
// without widening what the staff token itself may do. A plain contributor
// never gets one of these — their only session stays under TOKEN_KEY, same
// as before this feature existed.
const CONTRIB_TOKEN_KEY = 'vista.token.contributor';
// Which cabinet is on screen right now. Persisted so a reload lands back in
// the cabinet the person was just in, not always the highest-ranked one.
const ACTIVE_ROLE_KEY = 'vista.activeRole';

/** The always-present login token, unaffected by which cabinet is active.
 *  Used to bootstrap identity (api.mePrimary / api.asContributor) so a stale
 *  activeRole='contributor' left over from a previous session can never hide
 *  a staff person's own roles from them on reload. */
export const getPrimaryToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);

export const getContributorToken = (): string | null => localStorage.getItem(CONTRIB_TOKEN_KEY);
export const setContributorToken = (t: string): void => localStorage.setItem(CONTRIB_TOKEN_KEY, t);

export const getActiveRole = (): string | null => localStorage.getItem(ACTIVE_ROLE_KEY);
export const setActiveRole = (role: string): void => localStorage.setItem(ACTIVE_ROLE_KEY, role);

/**
 * The token for the CURRENTLY ACTIVE cabinet. Exported as `getToken` — not a
 * new name — because src/capture/vistaR2Stream.ts reads it directly for the
 * raw multipart PUTs it fires while streaming a recording, and that file is
 * not part of this feature's edit surface. Those PUTs hit /capture/part,
 * which is contributor-only server-side (requireContributor); a staff person
 * filming under the switched-to contributor cabinet needs the contributor
 * token on that request too, so this one function has to serve both paths.
 */
export const getToken = (): string | null => {
  if (getActiveRole() === 'contributor') {
    const c = getContributorToken();
    if (c) return c;
  }
  return getPrimaryToken();
};

export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CONTRIB_TOKEN_KEY);
  localStorage.removeItem(ACTIVE_ROLE_KEY);
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    // The parsed error body — domain errors carry context beyond the code
    // (e.g. rate_above_admin returns maxCents so the UI can name the ceiling).
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(code);
  }
}

/** A contract mismatch is a bug, not a user-facing state — throw and let the
 *  screen's error path show its generic message. */
export class ApiContractError extends Error {
  constructor(readonly route: string) {
    super(`api contract mismatch: ${route}`);
  }
}

function parseRes<T>(schema: ZodType<T>, data: unknown, route: string): T {
  const r = schema.safeParse(data);
  if (!r.success) {
    // Issue paths only — response bodies carry contributor PII.
    const issues = r.error.issues.map((i) => `${i.path.join('.')}:${i.code}`).join(', ');
    console.warn(`[contract] ${route} drift: ${issues}`);
    throw new ApiContractError(route);
  }
  return r.data;
}

/**
 * `tokenOverride` bypasses the role-aware getToken() for the two calls that
 * must ALWAYS use the primary/staff token regardless of which cabinet is on
 * screen: api.mePrimary() (establishes the full role set on boot) and
 * api.asContributor() (only a staff token can mint a contributor one).
 * `undefined` (the default) means "use getToken() as usual".
 */
async function call(path: string, init?: RequestInit, tokenOverride?: string | null): Promise<unknown> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  // Was this call actually riding the CONTRIBUTOR half of an F3 switch (a
  // staff person currently in the contributor cabinet), as opposed to the
  // primary/staff token or an explicit override? Matters below: a 401 on the
  // switched-in contributor session must not take the staff login down with it.
  const usingContributor =
    tokenOverride === undefined && getActiveRole() === 'contributor' && !!getContributorToken();
  const token = tokenOverride !== undefined ? tokenOverride : getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}/api/vista${path}`, { ...init, headers });
  if (res.status === 401) {
    // F3 cross-role bug (vista role audit, 12.08): a 401 here used to call
    // clearToken() unconditionally, which wipes BOTH tokens and forces
    // #/login. That is correct when the PRIMARY session died — but if a
    // staff person switched into their contributor cabinet and THAT one
    // specifically dies (contributor row blocked, or just a stale/expired
    // contributor JWT reused from a much earlier switch), their still-valid
    // staff login was getting logged out too, only because the OTHER
    // cabinet's token failed. Drop just the broken half and reload: the
    // primary session survives, and boot re-derives activeRole from
    // /auth/me's fresh `roles` (falls back off 'contributor' automatically —
    // see main.tsx's stored-role check).
    if (usingContributor && getPrimaryToken()) {
      localStorage.removeItem(CONTRIB_TOKEN_KEY);
      localStorage.removeItem(ACTIVE_ROLE_KEY);
      location.reload();
      throw new ApiError(401, 'unauthorized');
    }
    // Primary session died (expired, account disabled). Drop it and land on
    // /login instead of every screen handling 401 separately.
    clearToken();
    location.hash = '#/login';
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    throw new ApiError(res.status, (body?.error as string) ?? `http_${res.status}`, body);
  }
  return res.json();
}

const get = async <T>(path: string, schema: ZodType<T>, tokenOverride?: string | null): Promise<T> =>
  parseRes(schema, await call(path, undefined, tokenOverride), `GET ${path}`);

const send = async <T>(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body: unknown,
  schema: ZodType<T>,
  tokenOverride?: string | null,
): Promise<T> =>
  parseRes(
    schema,
    await call(path, { method, body: JSON.stringify(body) }, tokenOverride),
    `${method} ${path}`,
  );

/** Escape hatch for endpoints whose responses are not worth a contract yet
 *  (the capture ingest, whose shapes are pinned worker-side). Everything the
 *  dashboards render goes through `api` and its schemas instead. */
export const apiPost = (path: string, body: unknown): Promise<unknown> =>
  call(path, { method: 'POST', body: JSON.stringify(body) });

export const api = {
  me: () => get('/auth/me', VistaMeResSchema),
  // F3 — bypasses getToken()'s role-awareness: always the primary/staff
  // token, so a reload mid-switch (activeRole='contributor' in storage)
  // still comes back with the full staff `roles` set, not the contributor's.
  mePrimary: () => get('/auth/me', VistaMeResSchema, getPrimaryToken()),
  // POST with an empty body — the caller IS the account being exchanged, see
  // VistaAsContributorReqSchema. Reuses VistaLoginResSchema: both responses
  // are just "here is a token". Forces the primary token for the same reason
  // as mePrimary — this call is only valid FROM a staff session.
  asContributor: () => send('POST', '/auth/as-contributor', {}, VistaLoginResSchema, getPrimaryToken()),
  // F4 — changes whichever account is CURRENTLY active (staff or
  // contributor), so this one deliberately uses the default getToken().
  changePassword: (password: string) =>
    send('POST', '/auth/password', { password }, VistaOkChangedResSchema),
  tasks: () => get('/tasks', VistaTasksResSchema),

  // ── clip queues ───────────────────────────────────────────────────────────
  captures: (
    q: {
      status?: string;
      contributorId?: string;
      from?: string;
      to?: string;
      q?: string;
      limit?: number;
    } = {},
  ) => {
    const p = new URLSearchParams();
    if (q.status) p.set('status', q.status);
    if (q.contributorId) p.set('contributorId', q.contributorId);
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.q) p.set('q', q.q);
    if (q.limit) p.set('limit', String(q.limit));
    const qs = p.toString();
    return get(`/captures${qs ? `?${qs}` : ''}`, VistaCapturesResSchema);
  },
  moveCaptures: (body: {
    ids: string[];
    move: VistaCaptureMove;
    reason?: VistaRejectReason;
    note?: string;
  }) => send('POST', '/captures/move', body, VistaCaptureMoveResSchema),
  /**
   * Разбор списка вердиктов клиента. Ничего не меняет — только показывает,
   * что применится; применяет потом обычный moveCaptures.
   */
  /** Оглавление фрагментов клипа — плеер по нему тянет только нужные куски. */
  captureIndex: (id: string, variant: 'raw' | 'voiced' = 'raw') =>
    get(
      `/capture/${encodeURIComponent(id)}/index${variant === 'voiced' ? '?variant=voiced' : ''}`,
      VistaCaptureIndexResSchema,
    ),
  verdictsPreview: (text: string) =>
    send('POST', '/captures/verdicts/preview', { text }, VistaVerdictsPreviewResSchema),
  /**
   * Direct link — used both by the plain "скачать" anchors and by
   * PlayerModal's <video src>. No fetch, no blob in memory, and the worker
   * streams Range requests straight through for seeking.
   *
   * The session token rides in the query string (on top of the normal
   * Authorization-header path, which every other call in this file uses):
   * a native <video>/<a> element cannot set a custom header, and the
   * browser's own Range re-requests while scrubbing go out exactly the same
   * way. This is the one deliberate exception to "no tokens in URL params" —
   * unavoidable for a real <video> element, and the worker only accepts it as
   * a fallback for this one capture-file route.
   */
  captureFileUrl: (id: string, variant: 'raw' | 'voiced' = 'raw'): string => {
    const token = getToken();
    const t = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${BASE}/api/vista/capture/${encodeURIComponent(id)}/file?variant=${variant}${t}`;
  },

  // ── money ─────────────────────────────────────────────────────────────────
  balances: () => get('/finance/balances', VistaBalancesResSchema),
  ledger: (target: { contributorId?: string; userId?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (target.contributorId) p.set('contributorId', target.contributorId);
    if (target.userId) p.set('userId', target.userId);
    if (target.limit) p.set('limit', String(target.limit));
    if (target.offset) p.set('offset', String(target.offset));
    return get(`/finance/ledger?${p.toString()}`, VistaLedgerResSchema);
  },
  payout: (body: {
    contributorId?: string;
    userId?: string;
    amountCents: number;
    note?: string;
    refKey: string;
  }) => send('POST', '/finance/payout', body, VistaPayoutResSchema),
  // Manual balance correction, manager-only — the reason is required server-side
  // (schema-enforced, min 3 chars), not just a UI nicety.
  adjust: (body: {
    contributorId?: string;
    userId?: string;
    amountCents: number;
    reason: string;
    refKey: string;
  }) => send('POST', '/finance/adjust', body, VistaAdjustResSchema),
  // F5 — одноразовая ссылка входа на 7 дней: человека не заставляют вводить
  // пароль с чужого листочка, он открывает ссылку на своём телефоне.
  invite: (contributorId: string) =>
    send('POST', '/manage/invite', { contributorId }, VistaInviteContributorResSchema),
  myStats: () => get('/my/stats', VistaMyStatsResSchema),
  // F6 — the contributor's own "what was this for" list, joined to the clip
  // that earned each accrual.
  myLedger: () => get('/my/ledger', VistaMyLedgerResSchema),
  login: (email: string, password: string, turnstileToken?: string) =>
    send('POST', '/auth/login', { email, password, turnstileToken }, VistaLoginResSchema),
  requestLink: (email: string, turnstileToken?: string) =>
    send('POST', '/auth/request', { email, turnstileToken }, VistaAuthRequestResSchema),
  verify: (token: string) => send('POST', '/auth/verify', { token }, VistaAuthVerifyResSchema),
  pool: () => get('/pool', VistaPoolResSchema),
  summary: () => get('/stats/summary', VistaSummaryResSchema),
  contributor: (id: string) => get(`/contributor/${id}`, VistaContributorResSchema),
  patchContributor: (id: string, body: unknown) =>
    send('PATCH', `/contributor/${id}`, body, VistaOkChangedResSchema),
  addContributors: (body: unknown) =>
    send('POST', '/contributors', body, VistaAddContributorsResSchema),
  assign: (contributorId: string, userId: string) =>
    send('POST', '/assign', { contributorId, userId }, VistaAssignResSchema),
  unassigned: () => get('/unassigned', VistaUnassignedResSchema),
  staff: () => get('/staff', VistaStaffListResSchema),
  createStaff: (body: unknown) => send('POST', '/staff', body, VistaCreateStaffResSchema),
  patchStaff: (id: string, body: unknown) =>
    send('PATCH', `/staff/${id}`, body, VistaOkChangedResSchema),
  adminStats: () => get('/stats/admins', VistaAdminStatsResSchema),
  unrated: () => get('/unrated', VistaUnratedResSchema),

  // ── F8: plan-to-deadline / pace ──────────────────────────────────────────
  pace: () => get('/pace', VistaPaceResSchema),
  setPaceGoal: (body: { targetHours: number; deadline: string }) =>
    send('PUT', '/pace/goal', body, VistaSetPaceGoalResSchema),

  // ── A3: "кто пишет прямо сейчас" ──────────────────────────────────────────
  liveNow: () => get('/live-now', VistaLiveNowResSchema),
};

export type {
  VistaAddContributorsRes,
  VistaAdminStatsRow,
  VistaContributorRes,
  VistaContributorMe,
  VistaMeRes,
  VistaMeRole,
  VistaMyStatsRes,
  VistaLiveNowRes,
  VistaPaceRes,
  VistaPoolRes,
  VistaPoolRow,
  VistaStaffListRes,
  VistaStaffMe,
  VistaSummaryRes,
  VistaUnassignedRes,
};
